import { TextTooltip } from '@components'
import { AccountingSuggestionKey, IAccountingSuggestion } from '@root/query/suggestions'
import { getImportSourceLabelKey } from '@root/query/documents'
import { formatCurrency, formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaClock, FaShare } from 'react-icons/fa'
import {
  HeaderActions,
  HeaderAmount,
  HeaderChipDate,
  HeaderChipRow,
  HeaderCompactIcon,
  HeaderMain,
  HeaderSourceEyebrow,
  HeaderStatusIcon,
  RuleWaitingInline,
  SuggestionHeader
} from '../styles'
import { SuggestionUncertaintyPopover } from '../uncertainty/SuggestionUncertaintyPopover'
import { useSuggestionUncertainty } from '../uncertainty/SuggestionUncertaintyContext'

interface SuggestionCardHeaderProps {
  suggestion: IAccountingSuggestion
}

const formatAccountLabel = (
  accountNumber?: string | number | null,
  accountName?: string | null
) => {
  const numberPart = accountNumber == null ? '' : String(accountNumber)
  const namePart = accountName ?? ''

  if (numberPart && namePart) return `${numberPart} ${namePart}`
  if (namePart) return namePart
  if (numberPart) return numberPart
  return '-'
}

export const SuggestionCardHeader: React.FC<SuggestionCardHeaderProps> = ({ suggestion }) => {
  const { t } = useTranslation()
  const { enabled, highlight } = useSuggestionUncertainty()
  const isRuleWaitingForAttachment = suggestion.key === AccountingSuggestionKey.RuleWaitAttachment
  const importSourceLabel = t(
    `document.importSource.hoverTitle.${getImportSourceLabelKey(
      suggestion.import_data.data_source
    )}`
  )
  const canUseAccrualSplit = suggestion.accrual_split.enabled
  const settlementAccountLabel = formatAccountLabel(
    suggestion.source_account?.number,
    suggestion.source_account?.name
  )

  return (
    <SuggestionHeader id={`suggestion-${suggestion.id}-header`} $highlight={highlight === 'header'}>
      {enabled ? (
        <HeaderCompactIcon $isExpense={suggestion.amount < 0}>
          <FaShare />
        </HeaderCompactIcon>
      ) : (
        <HeaderStatusIcon $isExpense={suggestion.amount < 0}>
          <FaShare />
        </HeaderStatusIcon>
      )}
      <HeaderMain>
        {enabled && !isRuleWaitingForAttachment && (
          <HeaderSourceEyebrow>
            <span>{importSourceLabel}</span>
            <span>·</span>
            <span>{formatDate(suggestion.date)}</span>
          </HeaderSourceEyebrow>
        )}
        <HeaderAmount>{formatCurrency(suggestion.amount)}</HeaderAmount>
        {(!enabled || isRuleWaitingForAttachment) && (
          <HeaderChipRow>
            {isRuleWaitingForAttachment ? (
              <TextTooltip
                tooltip={t('document.suggestionCard.ruleWaitingForAttachmentHint')}
                openDelay={400}
                placement="bottom-start"
              >
                <RuleWaitingInline>
                  <FaClock aria-hidden />
                  <span>{t('document.suggestionCard.ruleWaitingForAttachmentBadge')}</span>
                </RuleWaitingInline>
              </TextTooltip>
            ) : canUseAccrualSplit ? (
              <span>{importSourceLabel}</span>
            ) : (
              <>
                <HeaderChipDate>{formatDate(suggestion.date)}</HeaderChipDate>
                <span aria-hidden>•</span>
                <span>{settlementAccountLabel}</span>
                <span aria-hidden>•</span>
                <span>{importSourceLabel}</span>
              </>
            )}
          </HeaderChipRow>
        )}
      </HeaderMain>
      {enabled && (
        <HeaderActions>
          <SuggestionUncertaintyPopover suggestion={suggestion} />
        </HeaderActions>
      )}
    </SuggestionHeader>
  )
}
